import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import {
  EnergyHistoricalDataState,
  ExtendedRawConsumptionProperties,
  SavedSelectedFeature,
} from '@store';

import { HemsProperties, ViError } from '@vi-iot/client';

import { AppState } from '../app.state';

import { FilterType } from './../../+installations/installation/energy-historical-data-modal/energy-filters-header/energy-filters.data';
import { HemsFlowType } from './energy-historical-data.helpers';
import { HemsHistoricalData, LegendFeature } from './energy-historical-data.model';

import Moment = moment.Moment;

export namespace EnergyHistoricalDataSelectors {
  export const energyHistoricalData = (state: AppState): EnergyHistoricalDataState =>
    state.energyHistoricalData;

  export function getState(): MemoizedSelector<
    AppState,
    EnergyHistoricalDataState,
    DefaultProjectorFn<EnergyHistoricalDataState>
  > {
    return createSelector(energyHistoricalData, (state: EnergyHistoricalDataState) => state);
  }

  export function selectHemsAnalysisSelectedTab(): MemoizedSelector<
    AppState,
    HemsFlowType,
    DefaultProjectorFn<HemsFlowType>
  > {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.hemsAnalysisSelectedTab
    );
  }

  export const getLegacyChartData = (): MemoizedSelector<
    AppState,
    ExtendedRawConsumptionProperties[],
    DefaultProjectorFn<ExtendedRawConsumptionProperties[]>
  > =>
    createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState): ExtendedRawConsumptionProperties[] =>
        state.legacyChartData
    );

  export function selectChartData(): MemoizedSelector<
    AppState,
    HemsHistoricalData,
    DefaultProjectorFn<HemsHistoricalData>
  > {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.chartData
    );
  }

  export function selectFilterType(): MemoizedSelector<
    AppState,
    FilterType,
    DefaultProjectorFn<FilterType>
  > {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.filterType
    );
  }

  export function selectFilterDate(): MemoizedSelector<
    AppState,
    Moment,
    DefaultProjectorFn<Moment>
  > {
    return createSelector(energyHistoricalData, (state: EnergyHistoricalDataState) => state.date);
  }

  export function selectSelectedFeatures(): MemoizedSelector<AppState, HemsProperties[]> {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.selectedFeatures
    );
  }

  export function selectSavedSelectedFeatures(): MemoizedSelector<AppState, SavedSelectedFeature> {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.savedSelectedFeature
    );
  }

  export function selectSavedSelectedFeaturesForInstallationId(
    installationId: string
  ): MemoizedSelector<
    AppState,
    {
      electricitySelectedFeatures: HemsProperties[];
      heatingSelectedFeatures: HemsProperties[];
    }
  > {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.savedSelectedFeature?.[installationId]
    );
  }

  export function selectLegendFeatures(): MemoizedSelector<AppState, LegendFeature[]> {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.legendFeatures
    );
  }

  export function getLoading(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      energyHistoricalData,
      (state: EnergyHistoricalDataState) => state.loading
    );
  }

  export function selectError(): MemoizedSelector<AppState, ViError, DefaultProjectorFn<ViError>> {
    return createSelector(energyHistoricalData, (state: EnergyHistoricalDataState) => state.error);
  }
}
